import React, { useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { api } from '../services/api';
import CodeInput from './CodeInput';
import QRCode from 'qrcode';

const AccountDetails = ({ accountData, canManageAccount, onUpdated }) => {
  const [fullName, setFullName] = useState(accountData.full_name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(accountData.phone_number || '');
  const [phoneToken, setPhoneToken] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);
  const [mfaPreference, setMfaPreference] = useState(accountData.mfa_preference || 'auto');
  const [totpEnabled, setTotpEnabled] = useState(Boolean(accountData.totp_enabled));
  const [totpSecret, setTotpSecret] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpQrData, setTotpQrData] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpBackupCodes, setTotpBackupCodes] = useState([]);
  const [totpSetupLoading, setTotpSetupLoading] = useState(false);
  const [totpVerifyLoading, setTotpVerifyLoading] = useState(false);
  const [totpDisablePassword, setTotpDisablePassword] = useState('');
  const [totpDisableCode, setTotpDisableCode] = useState('');
  const [totpDisableToken, setTotpDisableToken] = useState('');
  const [totpDisableLoading, setTotpDisableLoading] = useState(false);
  const [totpDisableRequesting, setTotpDisableRequesting] = useState(false);
  const [totpRegenerating, setTotpRegenerating] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });
  const [saving, setSaving] = useState(false);
  const phoneVerified = Boolean(accountData.phone_verified_at);
  const totpVerifiedAt = accountData.totp_verified_at ? new Date(accountData.totp_verified_at) : null;
  const normalizePhoneForCompare = (value) => {
    if (!value) return '';
    const digits = value.replace(/[^\d]/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    return digits;
  };
  const normalizedInputPhone = normalizePhoneForCompare(phoneNumber);
  const normalizedStoredPhone = normalizePhoneForCompare(accountData.phone_number || '');
  const hasPhoneChange = normalizedInputPhone !== normalizedStoredPhone;
  const isUsPhoneInput = /^\d{10}$/.test(normalizedInputPhone);
  const hasProfileChanges = fullName !== (accountData.full_name || '')
    || mfaPreference !== (accountData.mfa_preference || 'auto');
  const canUseMfaSettings = accountData.password_login_enabled
    || accountData.mfa_required
    || accountData.mfa_require_oauth
    || ['totp_only', 'totp_preferred'].includes(accountData.mfa_channel_policy)
    || totpEnabled;

  useEffect(() => {
    setFullName(accountData.full_name || '');
    setPhoneNumber(accountData.phone_number || '');
    setMfaPreference(accountData.mfa_preference || 'auto');
    setTotpEnabled(Boolean(accountData.totp_enabled));
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTotpDisablePassword('');
    setTotpDisableCode('');
    setTotpDisableToken('');
    setPasswordStatus({ type: '', message: '' });
  }, [accountData.full_name, accountData.phone_number, accountData.mfa_preference, accountData.totp_enabled]);

  useEffect(() => {
    setPhoneToken('');
    setPhoneCode('');
    setPhoneResendCooldown(0);
  }, [phoneNumber]);

  useEffect(() => {
    if (!totpUri) {
      setTotpQrData('');
      return;
    }
    QRCode.toDataURL(totpUri, { width: 220, margin: 1 })
      .then((url) => setTotpQrData(url))
      .catch(() => setTotpQrData(''));
  }, [totpUri]);

  useEffect(() => {
    if (!phoneResendCooldown) return undefined;
    const timer = setInterval(() => {
      setPhoneResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [phoneResendCooldown]);

  useEffect(() => {
    if (!hasProfileChanges || saving) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasProfileChanges, saving]);

  const handlePhoneRequest = async () => {
    setStatus({ type: '', message: '' });
    setPhoneSending(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/user/phone/request', { phone_number: phoneNumber });
      setPhoneToken(response.data?.phone_token || '');
      setPhoneResendCooldown(30);
      setStatus({ type: 'success', message: 'Verification code sent to your phone.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send verification code.' });
    } finally {
      setPhoneSending(false);
    }
  };

  const handlePhoneVerify = async () => {
    setStatus({ type: '', message: '' });
    setPhoneVerifying(true);
    try {
      await api.post('/acct-mgmt/v1/account/user/phone/verify', {
        phone_number: phoneNumber,
        code: phoneCode,
        phone_token: phoneToken,
      });
      setPhoneCode('');
      setPhoneToken('');
      setPhoneResendCooldown(0);
      setStatus({ type: 'success', message: 'Phone number verified.' });
      onUpdated();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to verify phone number.' });
    } finally {
      setPhoneVerifying(false);
    }
  };

  const handleTotpSetup = async () => {
    setStatus({ type: '', message: '' });
    setTotpSetupLoading(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/user/totp/setup');
      setTotpSecret(response.data?.secret || '');
      setTotpUri(response.data?.otpauth_uri || '');
      setTotpCode('');
      setTotpBackupCodes([]);
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to start authenticator setup.' });
    } finally {
      setTotpSetupLoading(false);
    }
  };

  const handleTotpVerify = async () => {
    setStatus({ type: '', message: '' });
    setTotpVerifyLoading(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/user/totp/verify', { code: totpCode });
      setTotpBackupCodes(response.data?.backup_codes || []);
      setTotpEnabled(true);
      setTotpSecret('');
      setTotpUri('');
      setTotpCode('');
      onUpdated();
      setStatus({ type: 'success', message: 'Authenticator enabled.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Authenticator verification failed.' });
    } finally {
      setTotpVerifyLoading(false);
    }
  };

  const handleTotpRegenerate = async () => {
    setStatus({ type: '', message: '' });
    setTotpRegenerating(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/user/totp/backup/regenerate');
      setTotpBackupCodes(response.data?.backup_codes || []);
      setStatus({ type: 'success', message: 'Backup codes regenerated.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to regenerate backup codes.' });
    } finally {
      setTotpRegenerating(false);
    }
  };

  const handleTotpDisableRequest = async () => {
    setStatus({ type: '', message: '' });
    setTotpDisableRequesting(true);
    try {
      const response = await api.post('/acct-mgmt/v1/account/user/totp/disable/request');
      setTotpDisableToken(response.data?.verification_token || '');
      setTotpDisableCode('');
      setStatus({ type: 'success', message: 'Verification code sent to your email.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send verification code.' });
    } finally {
      setTotpDisableRequesting(false);
    }
  };

  const handleTotpDisable = async () => {
    setStatus({ type: '', message: '' });
    setTotpDisableLoading(true);
    try {
      await api.post('/acct-mgmt/v1/account/user/totp/disable', accountData.password_set
        ? { password: totpDisablePassword }
        : { code: totpDisableCode, verification_token: totpDisableToken });
      setTotpEnabled(false);
      setTotpDisablePassword('');
      setTotpDisableCode('');
      setTotpDisableToken('');
      setTotpBackupCodes([]);
      setTotpSecret('');
      setTotpUri('');
      onUpdated();
      setStatus({ type: 'success', message: 'Authenticator disabled.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to disable authenticator.' });
    } finally {
      setTotpDisableLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setSaving(true);

    const payload = {};
    if (fullName !== accountData.full_name) payload.full_name = fullName;
    if (mfaPreference !== accountData.mfa_preference) payload.mfa_preference = mfaPreference;

    if (Object.keys(payload).length === 0) {
      setStatus({ type: 'warning', message: 'No changes to save.' });
      setSaving(false);
      return;
    }

    try {
      await api.put('/acct-mgmt/v1/account/user', payload);
      const passwordUpdated = Boolean(payload.new_password);
      setStatus({ type: 'success', message: passwordUpdated ? 'Password updated.' : 'Account details updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onUpdated();
    } catch (err) {
      const rawError = err.response?.data?.error || err.response?.data?.message || err.message;
      const message = rawError === 'Current password is incorrect'
        ? 'Current password is incorrect. Please try again.'
        : (rawError || 'Failed to update account.');
      setStatus({ type: 'danger', message });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordStatus({ type: '', message: '' });

    if (!newPassword || !confirmPassword) {
      setPasswordStatus({ type: 'danger', message: 'Enter and confirm your new password.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'danger', message: 'New password and confirmation do not match.' });
      return;
    }
    if (accountData.password_set && !currentPassword) {
      setPasswordStatus({ type: 'danger', message: 'Enter your current password to update it.' });
      return;
    }
    if (accountData.password_set && currentPassword && newPassword === currentPassword) {
      setPasswordStatus({ type: 'danger', message: 'New password must be different from the current password.' });
      return;
    }

    try {
      await api.put('/acct-mgmt/v1/account/user', {
        new_password: newPassword,
        current_password: accountData.password_set ? currentPassword : undefined,
      });
      setPasswordStatus({ type: 'success', message: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onUpdated();
    } catch (err) {
      const rawError = err.response?.data?.error || err.response?.data?.message || err.message;
      const message = rawError === 'Current password is incorrect'
        ? 'Current password is incorrect. Please try again.'
        : (rawError || 'Failed to update password.');
      setPasswordStatus({ type: 'danger', message });
    }
  };

  return (
    <div>
      <Prompt
        when={hasProfileChanges && !saving}
        message="You have unsaved changes. Continue without saving?"
      />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Account Details</h2>
          <p className="text-muted mb-0">Manage your profile and security settings.</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasProfileChanges && <span className="text-warning small">Unsaved changes</span>}
          <button
            className="btn btn-primary"
            type="submit"
            form="account-details-form"
            disabled={saving || !hasProfileChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      {status.message && (
        <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
      )}
      <form id="account-details-form" onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Profile</h5>
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input className="form-control" value={accountData.email} disabled />
            </div>
            <div className="mb-0">
              <label className="form-label">Full Name</label>
              <input
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {canUseMfaSettings && (
          <div className="card mb-4">
          <div className="card-body">
              <h5 className="card-title">Multi-Factor Authentication</h5>
            {accountData.sms_enabled && (
                <div className="mb-4">
                  <div className="fw-semibold mb-2">Phone Number (SMS Verification)</div>
                  <div className="mb-3">
                    <label className="form-label d-flex align-items-center justify-content-between">
                      <span>Phone Number</span>
                      {phoneVerified && !hasPhoneChange && (
                        <span className="text-success d-flex align-items-center gap-1 small">
                          <i className="fas fa-check-circle" aria-hidden="true"></i>
                          Verified
                        </span>
                      )}
                    </label>
                    <input
                      className="form-control"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="(555) 555-5555"
                    />
                    {!phoneVerified || hasPhoneChange ? (
                      <div className="form-text text-muted">US numbers only. Verify your phone to enable SMS MFA.</div>
                    ) : null}
                    <div className="form-text text-muted">
                      By enabling SMS verification, you consent to receive security codes by text message. Message and data rates may apply.
                    </div>
                    {phoneVerified && !hasPhoneChange && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mt-2"
                        onClick={async () => {
                          setStatus({ type: '', message: '' });
                          try {
                            await api.post('/acct-mgmt/v1/account/user/phone/clear');
                            setPhoneNumber('');
                            setMfaPreference('auto');
                            setStatus({ type: 'success', message: 'Phone number removed.' });
                            onUpdated();
                          } catch (err) {
                            setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to remove phone number.' });
                          }
                        }}
                      >
                        Remove Phone
                      </button>
                    )}
                  </div>
                  {hasPhoneChange && (
                    <div className="mb-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={handlePhoneRequest}
                        disabled={phoneSending || !isUsPhoneInput || phoneResendCooldown > 0}
                      >
                        {phoneResendCooldown > 0 ? `Resend in ${phoneResendCooldown}s` : (phoneToken ? 'Resend Code' : 'Send Verification Code')}
                      </button>
                    </div>
                  )}
                  {hasPhoneChange && (
                    <div className="form-text text-muted mb-2">
                      Phone changes are saved only after verification.
                    </div>
                  )}
                  {hasPhoneChange && phoneToken && (
                    <div className="mb-3 text-center">
                      <CodeInput
                        value={phoneCode}
                        onChange={setPhoneCode}
                        length={6}
                        className="justify-content-center"
                      />
                      <button
                        type="button"
                        className="btn btn-primary mt-3"
                        disabled={phoneCode.length !== 6 || phoneVerifying}
                        onClick={handlePhoneVerify}
                      >
                        {phoneVerifying ? 'Verifying...' : 'Verify Phone'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <div className="fw-semibold mb-2">Authenticator App</div>
                {totpEnabled ? (
                  <>
                    <div className="d-flex align-items-center gap-2 text-success small mb-2">
                      <i className="fas fa-check-circle" aria-hidden="true"></i>
                      <span>
                        Authenticator app is enabled for this account
                      {totpVerifiedAt ? ` (set up ${totpVerifiedAt.toLocaleDateString()}).` : '.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleTotpRegenerate}
                      disabled={totpRegenerating}
                    >
                      {totpRegenerating ? 'Generating...' : 'Regenerate Backup Codes'}
                    </button>
                    <div className="mt-3">
                      <label className="form-label">Disable Authenticator App</label>
                      {accountData.password_set ? (
                        <>
                          <input
                            type="password"
                            className="form-control"
                            value={totpDisablePassword}
                            onChange={(e) => setTotpDisablePassword(e.target.value)}
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm mt-2"
                            onClick={handleTotpDisable}
                            disabled={!totpDisablePassword || totpDisableLoading}
                          >
                            {totpDisableLoading ? 'Disabling...' : 'Disable'}
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="form-text text-muted mb-2">
                            Confirm by email because this account does not have a Finbase password.
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handleTotpDisableRequest}
                            disabled={totpDisableRequesting}
                          >
                            {totpDisableRequesting ? 'Sending...' : (totpDisableToken ? 'Resend Code' : 'Send Verification Code')}
                          </button>
                          {totpDisableToken && (
                            <div className="mt-3 text-center">
                              <CodeInput
                                value={totpDisableCode}
                                onChange={setTotpDisableCode}
                                length={6}
                                className="justify-content-center"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm mt-3"
                                onClick={handleTotpDisable}
                                disabled={totpDisableCode.length !== 6 || totpDisableLoading}
                              >
                                {totpDisableLoading ? 'Disabling...' : 'Disable'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-text text-muted mb-2">Set up an authenticator app to generate verification codes.</div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleTotpSetup}
                      disabled={totpSetupLoading}
                    >
                      {totpSetupLoading ? 'Loading...' : 'Set Up Authenticator'}
                    </button>
                  </>
                )}
                {!totpEnabled && (totpSecret || totpUri) && (
                  <div className="mt-3">
                    <div className="text-muted small mb-2 text-center">
                      Use any authenticator app (Apple Passwords, Google Authenticator, Microsoft Authenticator, Authy, 1Password, Bitwarden, Duo).
                    </div>
                    {totpQrData && (
                      <div className="text-center mb-3">
                        <img src={totpQrData} alt="Authenticator QR code" />
                      </div>
                    )}
                    {totpSecret && (
                      <div className="text-center mb-3">
                        <div className="text-muted small mb-1">Manual setup key</div>
                        <div className="fw-semibold">{totpSecret}</div>
                      </div>
                    )}
                    <div className="text-center mb-2">
                      <label className="form-label text-center d-block">Verification Code</label>
                      <input
                        className="form-control text-center"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        placeholder="6-digit code"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleTotpVerify}
                      disabled={!totpCode || totpVerifyLoading}
                    >
                      {totpVerifyLoading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                  </div>
                )}
                {totpBackupCodes.length > 0 && (
                  <div className="mt-3">
                    <div className="fw-semibold mb-2">Backup Codes</div>
                    <div className="d-flex flex-column gap-1">
                      {totpBackupCodes.map((code) => (
                        <div key={code} className="font-monospace">{code}</div>
                      ))}
                    </div>
                    <div className="text-muted small mt-2">Save these codes now. They will not be shown again.</div>
                  </div>
                )}
              </div>

              <div>
                <div className="fw-semibold mb-2">MFA Preference</div>
                <select
                  className="form-select"
                  value={mfaPreference}
                  onChange={(e) => setMfaPreference(e.target.value)}
                  disabled={['email_only', 'sms_only', 'totp_only'].includes(accountData.mfa_channel_policy)}
                >
                  <option value="auto">Auto (Recommended)</option>
                  {accountData.sms_enabled && (
                    <option value="sms" disabled={!phoneVerified}>SMS</option>
                  )}
                  <option value="totp" disabled={!totpEnabled}>Authenticator App</option>
                  <option value="email">Email</option>
                </select>
                {accountData.sms_enabled && !phoneVerified && (
                  <div className="form-text text-muted">Verify a phone number to enable SMS preference.</div>
                )}
                {!totpEnabled && (
                  <div className="form-text text-muted">Enable an authenticator app to use TOTP preference.</div>
                )}
                {accountData.mfa_required && accountData.mfa_channel_policy === 'sms_only' && (
                  <div className="form-text text-muted">Your organization requires SMS for MFA.</div>
                )}
                {accountData.mfa_required && accountData.mfa_channel_policy === 'totp_only' && (
                  <div className="form-text text-muted">Your organization requires an authenticator app for MFA.</div>
                )}
                {accountData.mfa_required && accountData.mfa_channel_policy === 'email_only' && (
                  <div className="form-text text-muted">Your organization requires email for MFA.</div>
                )}
              </div>
            </div>
          </div>
        )}

      </form>

      {accountData.password_login_enabled && (
        <div className="card mt-3 mb-4">
          <div className="card-body">
            <h5 className="card-title">Password Reset</h5>
            {passwordStatus.message && (
              <div className={`alert alert-${passwordStatus.type}`} role="alert">{passwordStatus.message}</div>
            )}
            {accountData.password_set ? (
              <div className="mb-3">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
            ) : (
              <div className="form-text text-muted mb-2">Password not currently set.</div>
            )}
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
              />
            </div>
            <div className="form-text mb-3">Minimum 8 characters, 1 uppercase, 1 special character.</div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-primary" type="button" onClick={handlePasswordUpdate}>
                Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetails;
